import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ArrowRight, ArrowLeft, Check, Loader2, AlertCircle, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { LocationPicker } from '../components/LocationPicker';
import { BuyCreditsModal } from '../components/BuyCreditsModal';
import { CalculationProgressModal } from '../components/CalculationProgressModal';
import { DecimalInput } from '../components/DecimalInput';
import { LossInput } from '../components/LossInput';
import { DetectionSummaryModal } from '../components/DetectionSummaryModal';
import { DetectionLoadingModal } from '../components/DetectionLoadingModal';
import { Project, SystemConfig, FinancialParams, Location, SolarData } from '../types';
import { fetchSolarData, reverseGeocode } from '../utils/nasaAPI';
import { calculateEnergyProduction, calculateFinancialMetrics, calculateEnvironmentalImpact, getDefaultLosses, getInverterEfficiency, autoDetectLosses } from '../utils/calculations';
import { createProject } from '../utils/api';
import { generateProjectId } from '../utils/storage';
import { useAuth } from '../contexts/AuthContext';
import { SEO, SEO_CONFIGS } from '../components/SEO';

const STEPS = [
  { id: 'location', title: 'Location', description: 'Select project location' },
  { id: 'system', title: 'System Design', description: 'Configure PV system' },
  { id: 'financial', title: 'Economic Parameters', description: 'Set financial details' },
  { id: 'review', title: 'Review & Calculate', description: 'Verify and create analysis' },
];

const STORAGE_KEY = 'newAnalysisFormData';

// Helper function to safely parse float values
const safeParseFloat = (value: string, fallback: number = 0): number => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? fallback : parsed;
};

// Helper to save form data to localStorage
const saveFormData = (data: {
  location: Location;
  systemConfig: SystemConfig;
  financialParams: FinancialParams;
  currentStep: number;
}) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save form data:', error);
  }
};

// Helper to load form data from localStorage
const loadFormData = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error('Failed to load form data:', error);
  }
  return null;
};

// Helper to clear saved form data
const clearFormData = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear form data:', error);
  }
};

// Helper to generate project name based on location and system
const generateProjectName = (location: Location, systemCapacity: number): string => {
  let locationName = '';
  
  // Priority: City > Short Country > Coordinates
  if (location.city) {
    locationName = location.city;
  } else if (location.country) {
    // Shorten long country names
    const countryShortNames: Record<string, string> = {
      'United Arab Emirates': 'UAE',
      'United States': 'USA',
      'United Kingdom': 'UK',
      'Saudi Arabia': 'KSA',
      'South Africa': 'SA',
      'Birleşik Arap Emirlikleri': 'BAE',
      'Türkiye': 'TR',
      'Deutschland': 'DE',
      'España': 'ES',
    };
    
    locationName = countryShortNames[location.country] || location.country;
    
    // If still too long, truncate
    if (locationName.length > 15) {
      locationName = locationName.substring(0, 15);
    }
  } else {
    // Use coordinates as fallback
    locationName = `${location.latitude.toFixed(2)}°N`;
  }
  
  return `${locationName} ${systemCapacity}kWp`;
};

export function NewAnalysis() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { credits, refreshCredits, user } = useAuth();
  
  // Form data - ALL useState hooks must come first!
  const [location, setLocation] = useState<Location>({
    latitude: 24.4539,  // Abu Dhabi
    longitude: 54.3773, // Abu Dhabi
  });
  
  const [systemConfig, setSystemConfig] = useState<SystemConfig>({
    capacity: 100,
    panelType: 'monocrystalline',
    systemType: 'grid-tied',
    inverterEfficiency: 97, // Grid-tied default
    losses: getDefaultLosses(),
  });

  const [financialParams, setFinancialParams] = useState({
    capex: {
      panelCost: 40000, // Changed from $/kWp to total $
      inverterCost: 15000,
      bosCost: 25000,
      softCosts: 10000,
    },
    opex: {
      maintenance: 2000,
      insurance: 1000,
      monitoring: 500,
    },
    electricityPrice: 0.12,
    electricityPriceEscalation: 2.5,
    discountRate: 5,
    projectLifetime: 25, // Fixed at 25 years - not editable by user
    currency: 'USD',
  });
  
  const [currentStep, setCurrentStep] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationStep, setCalculationStep] = useState<'fetching' | 'processing' | 'calculating' | 'complete'>('fetching');
  const [error, setError] = useState<string | null>(null);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [solarData, setSolarData] = useState<SolarData | null>(null);
  const [completedProjectId, setCompletedProjectId] = useState<string | null>(null);

  // Load form data from localStorage on mount - only once
  useEffect(() => {
    if (!isInitialized) {
      const savedData = loadFormData();
      if (savedData) {
        console.log('[NEW ANALYSIS] ✅ Restoring form data from localStorage:', savedData);
        setLocation(savedData.location);
        setSystemConfig(savedData.systemConfig);
        setFinancialParams(savedData.financialParams);
        setCurrentStep(savedData.currentStep);
        // No toast - user will see their data is restored naturally
      } else {
        console.log('[NEW ANALYSIS] No saved form data found');
      }
      setIsInitialized(true);
    }
  }, [isInitialized]);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveFormData({
        location,
        systemConfig,
        financialParams,
        currentStep,
      });
    }, 500); // Debounce to avoid excessive localStorage writes

    return () => clearTimeout(timeoutId);
  }, [location, systemConfig, financialParams, currentStep]);

  // Check for Stripe redirect params and open modal if needed
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    
    if (success === 'true' || canceled === 'true') {
      setShowCreditsModal(true);
    }
  }, [searchParams]);

  // Refresh credits when modal closes after successful purchase
  useEffect(() => {
    if (!showCreditsModal && searchParams.get('success') === 'true') {
      // Modal just closed after successful purchase, refresh credits
      refreshCredits();
    }
  }, [showCreditsModal, searchParams, refreshCredits]);

  // Auto-start calculation if user came from payment success page
  useEffect(() => {
    const autostart = searchParams.get('autostart');
    
    if (autostart === 'true' && isInitialized && !isCalculating) {
      console.log('[NEW ANALYSIS] 🚀 Auto-starting calculation from payment success...');
      
      // Check if we have saved form data
      const savedData = loadFormData();
      if (savedData && credits && credits.balance >= 1) {
        // DON'T show review screen - just start calculation immediately
        console.log('[NEW ANALYSIS] Starting calculation immediately (no review screen)...');
        
        // Start calculation without delay or toast
        handleCalculate();
      } else if (!savedData) {
        console.error('[NEW ANALYSIS] ❌ No saved form data found for autostart');
        setError('No saved form data found. Please fill out the form to create an analysis.');
      } else if (!credits || credits.balance < 1) {
        console.log('[NEW ANALYSIS] ⚠️ Insufficient credits for autostart, opening modal...');
        setShowCreditsModal(true);
      }
      
      // Clear the autostart param from URL
      navigate('/new', { replace: true });
    }
  }, [searchParams, isInitialized, isCalculating, credits]);

  const handleLocationChange = (lat: number, lon: number) => {
    setLocation({ ...location, latitude: lat, longitude: lon });
    // Clear cached solar data when location changes
    setSolarData(null);
  };

  const handleNext = async () => {
    // FIRST: Check credits BEFORE any API calls (skip for admins and super admins)
    if (currentStep === STEPS.length - 1) {
      // Admin and SuperAdmin NEVER need credit check - proceed without any restrictions
      if (!user?.isSuperAdmin && !user?.isAdmin) {
        // Regular users need credit check
        if (!credits || credits.balance < 1) {
          console.log('[NEW ANALYSIS] ⚠️ Insufficient credits, opening credits modal...');
          setShowCreditsModal(true);
          return; // Stop here - don't fetch NASA data or proceed with calculation
        }
      }
    }
    
    // Step 1: Location - Validate project name
    if (currentStep === 0) {
      // No project name validation needed in this step
    }
    
    // Step 2: System Design - Validate system capacity
    if (currentStep === 1) {
      if (!systemConfig.capacity || systemConfig.capacity <= 0) {
        console.error('[NEW ANALYSIS] ❌ Invalid system capacity:', systemConfig.capacity);
        setError('System capacity must be greater than 0 kW');
        return;
      }
      if (!systemConfig.inverterEfficiency || systemConfig.inverterEfficiency <= 0 || systemConfig.inverterEfficiency > 100) {
        console.error('[NEW ANALYSIS] ❌ Invalid inverter efficiency:', systemConfig.inverterEfficiency);
        setError('Inverter efficiency must be between 0% and 100%');
        return;
      }
    }
    
    // Step 3: Financial - Validate financial parameters
    if (currentStep === 2) {
      const totalCapex = financialParams.capex.panelCost + 
                         financialParams.capex.inverterCost + 
                         financialParams.capex.bosCost + 
                         financialParams.capex.softCosts;
      
      if (totalCapex <= 0) {
        console.error('[NEW ANALYSIS] ❌ Invalid CAPEX:', totalCapex);
        setError('Total CAPEX must be greater than 0');
        return;
      }
      
      if (!financialParams.electricityPrice || financialParams.electricityPrice <= 0) {
        console.error('[NEW ANALYSIS] ❌ Invalid electricity price:', financialParams.electricityPrice);
        setError('Electricity price must be greater than 0');
        return;
      }
    }
    
    setError(null);
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      await handleCalculate();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      // If on first step, go back to dashboard
      navigate('/');
    }
  };

  const handleCalculate = async () => {
    setIsCalculating(true);
    setError(null);
    setCalculationStep('fetching');

    try {
      console.log('\n╔════════════════════════════════════════════════════════════╗');
      console.log('║  CALCULATE & SAVE - START                                  ║');
      console.log('╚════════════════════════════════════════════════════════════╝');
      
      // Step 1: Fetch solar data from NASA API (use cache if available)
      let currentSolarData = solarData;
      
      console.log('📊 Solar Data Cache Status:', currentSolarData ? '✅ EXISTS' : '❌ NULL');
      
      if (!currentSolarData) {
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('⚠️  AUTO DETECT NOT USED - Fetching NASA data now...');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('[NEW ANALYSIS] 🛰️ Fetching solar data from NASA POWER API...');
        console.log('   Location:', location.latitude.toFixed(4) + '°, ' + location.longitude.toFixed(4) + '°');
        
        // Show modal step first
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Fetch NASA data
        currentSolarData = await fetchSolarData(location.latitude, location.longitude);
        setSolarData(currentSolarData);
        
        console.log('[NEW ANALYSIS] ✅ NASA data received and cached:', {
          avgTemp: currentSolarData.avgTemperature.toFixed(2) + '°C',
          avgPrecip: currentSolarData.avgPrecipitation.toFixed(2) + ' mm/day',
          avgHumidity: currentSolarData.avgHumidity.toFixed(1) + '%',
          annualGHI: currentSolarData.annualGHI.toFixed(0) + ' kWh/m²',
        });
        
        // Wait to show the data was fetched
        await new Promise(resolve => setTimeout(resolve, 1500));
      } else {
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('✅ AUTO DETECT WAS USED - Using cached NASA data');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('[NEW ANALYSIS] ✅ Using cached solar data from Auto Detect');
        console.log('   Cached Data:', {
          avgTemp: currentSolarData.avgTemperature.toFixed(2) + '°C',
          avgPrecip: currentSolarData.avgPrecipitation.toFixed(2) + ' mm/day',
          avgHumidity: currentSolarData.avgHumidity.toFixed(1) + '%',
          annualGHI: currentSolarData.annualGHI.toFixed(0) + ' kWh/m²',
        });
        // Skip the delay since we already have the data
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Step 2: Process solar radiation data
      setCalculationStep('processing');
      console.log('\n[NEW ANALYSIS] 🔄 Processing solar radiation data...');
      const geoData = await reverseGeocode(location.latitude, location.longitude);
      const updatedLocation = { ...location, ...geoData };

      // Calculate energy production
      const energyProduction = calculateEnergyProduction(currentSolarData, systemConfig);
      
      console.log('[NEW ANALYSIS] ✅ Energy production calculated:', {
        annualProduction: energyProduction.annualProduction.toFixed(0) + ' kWh',
        specificYield: energyProduction.specificYield.toFixed(0) + ' kWh/kWp',
        performanceRatio: energyProduction.performanceRatio.toFixed(1) + '%',
      });
      
      // Wait to show processing is happening
      await new Promise(resolve => setTimeout(resolve, 1200));

      // Step 3: Calculate financial metrics
      setCalculationStep('calculating');
      console.log('\n[NEW ANALYSIS] 💰 Calculating financial metrics...');
      const financialMetrics = calculateFinancialMetrics(energyProduction, financialParams, systemConfig.capacity);

      // Calculate environmental impact
      const environmentalImpact = calculateEnvironmentalImpact(energyProduction);
      
      console.log('[NEW ANALYSIS] ✅ Financial metrics calculated:', {
        NPV: '$' + financialMetrics.npv.toFixed(0),
        IRR: financialMetrics.irr.toFixed(2) + '%',
        payback: financialMetrics.paybackPeriod.toFixed(1) + ' years',
        LCOE: '$' + financialMetrics.lcoe.toFixed(4) + '/kWh',
      });
      
      // Wait to show calculations are running
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Step 4: Generate report
      setCalculationStep('complete');
      console.log('\n[NEW ANALYSIS] 📄 Generating comprehensive report...');
      
      // Create project
      const project: Project = {
        id: generateProjectId(),
        name: generateProjectName(updatedLocation, systemConfig.capacity),
        createdAt: new Date().toISOString(),
        location: updatedLocation,
        systemConfig,
        financialParams,
        results: {
          solarData: currentSolarData,
          energyProduction,
          financialMetrics,
          environmentalImpact,
        },
      };

      // Save project (this will deduct 1 credit on the backend)
      await createProject(project);

      // Refresh credits to show updated balance
      const newCredits = await refreshCredits();
      console.log('[NEW ANALYSIS] ✅ Analysis created successfully! Credit used. New balance:', newCredits?.balance);

      // Clear saved form data since project was successfully created
      clearFormData();

      // Set completed project ID to show success modal instead of auto-navigating
      setCompletedProjectId(project.id);
      setIsCalculating(false);
    } catch (err) {
      console.error('[NEW ANALYSIS] ❌ Calculate error:', err);
      const errorMessage = err instanceof Error ? err.message : 'An error occurred during calculation';
      
      // Check if this is an insufficient credits error
      if (errorMessage.includes('Insufficient credits') || errorMessage.includes('402')) {
        console.log('[NEW ANALYSIS] ⚠️ Insufficient credits error, opening modal...');
        setError('Insufficient credits. Please purchase credits to continue.');
        setShowCreditsModal(true);
      } else {
        console.error('[NEW ANALYSIS] ❌ Calculation failed:', errorMessage);
        setError(errorMessage);
      }
      
      setIsCalculating(false);
    }
  };

  return (
    <>
      <SEO {...SEO_CONFIGS.newAnalysis} />
      <div className="max-w-4xl mx-auto">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex-1 flex items-center">
                <div className="flex items-center gap-3">
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all
                      ${index < currentStep
                        ? 'bg-green-500 text-white'
                        : index === currentStep
                        ? 'bg-[#4F819F] text-white'
                        : 'bg-slate-200 text-slate-500'
                      }
                    `}
                  >
                    {index < currentStep ? <Check className="w-5 h-5" /> : index + 1}
                  </div>
                  <span
                    className={`text-sm font-medium ${
                      index <= currentStep ? 'text-slate-900' : 'text-slate-500'
                    }`}
                  >
                    {step.title}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`h-1 flex-1 mx-4 rounded ${
                      index < currentStep ? 'bg-green-500' : 'bg-slate-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-900">Error</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="bg-white rounded-xl border-2 border-slate-200 shadow-lg p-8 min-h-[500px]">
          {currentStep === 0 && (
            <StepLocation
              location={location}
              onLocationChange={handleLocationChange}
            />
          )}
          {currentStep === 1 && (
            <StepSystemDesign
              systemConfig={systemConfig}
              setSystemConfig={setSystemConfig}
              location={location}
              solarData={solarData}
              setSolarData={setSolarData}
            />
          )}
          {currentStep === 2 && (
            <StepFinancial
              financialParams={financialParams}
              setFinancialParams={setFinancialParams}
            />
          )}
          {currentStep === 3 && (
            <StepReview
              location={location}
              systemConfig={systemConfig}
              financialParams={financialParams}
            />
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="px-6 py-3 border-2 border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <button
            onClick={handleNext}
            disabled={isCalculating}
            className="px-6 py-3 bg-gradient-to-r from-[#4F819F] to-[#4E7F9D] text-white font-medium rounded-lg hover:from-[#456070] hover:to-[#4F819F] transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isCalculating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Calculating...
              </>
            ) : currentStep === STEPS.length - 1 ? (
              <>
                <Check className="w-4 h-4" />
                Calculate & Save
              </>
            ) : (
              <>
                Next
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>

        {/* Buy Credits Modal */}
        <BuyCreditsModal
          isOpen={showCreditsModal}
          onClose={() => setShowCreditsModal(false)}
        />

        {/* Calculation Progress Modal */}
        <CalculationProgressModal
          isOpen={isCalculating}
          solarData={solarData}
          location={location}
          calculationStep={calculationStep}
        />

        {/* Success Modal - Show when calculation is complete */}
        {completedProjectId && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="bg-white rounded-2xl border-2 border-slate-200 shadow-xl max-w-md w-full p-8 text-center"
            >
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Analysis Created!</h2>
              <p className="text-slate-600 mb-6">
                Your comprehensive solar investment analysis is ready to view
              </p>
              <button
                onClick={() => navigate(`/project/${completedProjectId}`)}
                className="w-full px-6 py-3 bg-gradient-to-r from-[#4F819F] to-[#4E7F9D] text-white font-medium rounded-xl hover:from-[#456070] hover:to-[#4F819F] transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                <Check className="w-5 h-5" />
                View Report
              </button>
            </motion.div>
          </div>
        )}
      </div>
    </>
  );
}

// Step 1: Location
function StepLocation({
  location,
  onLocationChange,
}: {
  location: Location;
  onLocationChange: (lat: number, lon: number) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-slate-900 mb-2">Location Selection</h3>
        <p className="text-slate-600">Select the installation location for your solar project</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Installation Location *
        </label>
        <p className="text-xs text-slate-500 mb-3">
          💡 Project name will be auto-generated based on location and system capacity
        </p>
        <LocationPicker
          latitude={location.latitude}
          longitude={location.longitude}
          onChange={onLocationChange}
        />
      </div>
    </div>
  );
}

// Step 2: System Design
function StepSystemDesign({
  systemConfig,
  setSystemConfig,
  location,
  solarData,
  setSolarData,
}: {
  systemConfig: SystemConfig;
  setSystemConfig: (config: SystemConfig) => void;
  location: Location;
  solarData: SolarData | null;
  setSolarData: (data: SolarData) => void;
}) {
  const [isAutoDetecting, setIsAutoDetecting] = useState(false);
  const [detectionStep, setDetectionStep] = useState(0);
  const [highlightInputs, setHighlightInputs] = useState(false);
  const [previousLosses, setPreviousLosses] = useState(systemConfig.losses);
  const [detectedLosses, setDetectedLosses] = useState(systemConfig.losses);
  const [showModal, setShowModal] = useState(false);

  // Auto-update inverter efficiency when system type changes
  const handleSystemTypeChange = (newSystemType: string) => {
    const recommendedEfficiency = getInverterEfficiency(newSystemType);
    setSystemConfig({ 
      ...systemConfig, 
      systemType: newSystemType as any,
      inverterEfficiency: recommendedEfficiency 
    });
  };

  // Auto-detect system losses with professional loading experience + NASA API
  const handleAutoDetect = async () => {
    setIsAutoDetecting(true);
    setDetectionStep(0);
    
    // Save current losses to show diff in modal
    setPreviousLosses({ ...systemConfig.losses });
    
    try {
      // Step 0: Initializing (800ms)
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Step 1: Fetch NASA data if not cached
      setDetectionStep(1);
      let currentSolarData = solarData;
      if (!currentSolarData) {
        console.log('[AUTO DETECT] 🛰️ Fetching NASA POWER API data...');
        await new Promise(resolve => setTimeout(resolve, 600)); // Show the step first
        
        currentSolarData = await fetchSolarData(location.latitude, location.longitude);
        setSolarData(currentSolarData);
        
        console.log('[AUTO DETECT] ✅ NASA data received:', {
          avgTemp: currentSolarData.avgTemperature,
          avgPrecip: currentSolarData.avgPrecipitation,
          avgHumidity: currentSolarData.avgHumidity,
        });
        
        await new Promise(resolve => setTimeout(resolve, 1200)); // Show received data
      } else {
        console.log('[AUTO DETECT] ✅ Using cached NASA data');
        await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate fetch time
      }
      
      // Step 2: Processing Climate Metrics (1000ms)
      setDetectionStep(2);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Step 3: Calculating Optimal Losses (1200ms)
      setDetectionStep(3);
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const newDetectedLosses = autoDetectLosses(
        location.latitude,
        location.longitude,
        systemConfig.systemType,
        systemConfig.panelType,
        systemConfig.capacity,
        currentSolarData.avgTemperature,
        currentSolarData.avgPrecipitation,
        currentSolarData.avgHumidity
      );
      
      console.log('[AUTO DETECT] ✅ Losses calculated:', newDetectedLosses);
      
      setDetectedLosses(newDetectedLosses);
      
      // Brief pause before showing results (400ms)
      await new Promise(resolve => setTimeout(resolve, 400));
      
      // Close loading, open results modal
      setIsAutoDetecting(false);
      setShowModal(true);
    } catch (error) {
      console.error('[AUTO DETECT] ❌ Error:', error);
      // Fallback: use estimates if NASA API fails
      setDetectionStep(2);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const fallbackLosses = autoDetectLosses(
        location.latitude,
        location.longitude,
        systemConfig.systemType,
        systemConfig.panelType,
        systemConfig.capacity
        // No NASA data - will use fallback estimates
      );
      
      setDetectedLosses(fallbackLosses);
      setIsAutoDetecting(false);
      setShowModal(true);
    }
  };

  // Apply detected losses to form
  const handleApplyChanges = () => {
    setHighlightInputs(true);
    setSystemConfig({
      ...systemConfig,
      losses: detectedLosses
    });
    setShowModal(false);
    
    // Remove highlight after animation
    setTimeout(() => {
      setHighlightInputs(false);
    }, 2000);
  };

  // Cancel - close modal without changes
  const handleCancel = () => {
    setShowModal(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-slate-900 mb-2">System Configuration</h3>
        <p className="text-slate-600">Define your solar PV system specifications</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            System Capacity (kWp) *
          </label>
          <DecimalInput
            value={systemConfig.capacity}
            onChange={(value) => setSystemConfig({ ...systemConfig, capacity: value })}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder="0"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Panel Type *
          </label>
          <select
            value={systemConfig.panelType}
            onChange={(e) => setSystemConfig({ ...systemConfig, panelType: e.target.value as any })}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          >
            <option value="monocrystalline">Monocrystalline (20% efficiency)</option>
            <option value="polycrystalline">Polycrystalline (17% efficiency)</option>
            <option value="thin-film">Thin-film (12% efficiency)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            System Type *
          </label>
          <select
            value={systemConfig.systemType}
            onChange={(e) => handleSystemTypeChange(e.target.value)}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          >
            <option value="grid-tied">Grid-tied (97% inverter efficiency)</option>
            <option value="hybrid">Hybrid (95% inverter efficiency)</option>
            <option value="off-grid">Off-grid (93% inverter efficiency)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Inverter Efficiency (%) *
          </label>
          <DecimalInput
            value={systemConfig.inverterEfficiency}
            onChange={(value) => setSystemConfig({ ...systemConfig, inverterEfficiency: value })}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder="0"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-slate-900">System Losses (%)</h4>
          <button
            onClick={handleAutoDetect}
            disabled={isAutoDetecting}
            className="group relative px-4 py-2 bg-gradient-to-r from-[#4F819F] to-[#6B9FB8] text-white text-sm font-medium rounded-lg hover:from-[#456070] hover:to-[#4F819F] transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            title="Auto-detect industry-standard losses based on location, system type, and panel type"
          >
            {isAutoDetecting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Detecting...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Auto Detect
              </>
            )}
            
            {/* Tooltip */}
            <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-64 p-2 bg-slate-900 text-white text-xs rounded-lg shadow-xl z-10">
              <p className="font-medium mb-1">AI-Powered Detection</p>
              <p>Automatically calculates optimal losses based on:</p>
              <ul className="mt-1 space-y-0.5 list-disc list-inside">
                <li>Climate zone (tropical, desert, temperate, cold)</li>
                <li>System type & panel technology</li>
                <li>System size & installation quality</li>
              </ul>
              <div className="absolute top-full right-4 -mt-1 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-900"></div>
            </div>
          </button>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <LossInput
            label="Soiling"
            value={systemConfig.losses.soiling}
            onChange={(value) => setSystemConfig({
              ...systemConfig,
              losses: { ...systemConfig.losses, soiling: value }
            })}
            highlight={highlightInputs}
          />
          
          <LossInput
            label="Shading"
            value={systemConfig.losses.shading}
            onChange={(value) => setSystemConfig({
              ...systemConfig,
              losses: { ...systemConfig.losses, shading: value }
            })}
            highlight={highlightInputs}
          />
          
          <LossInput
            label="DC Loss"
            value={systemConfig.losses.dcLoss}
            onChange={(value) => setSystemConfig({
              ...systemConfig,
              losses: { ...systemConfig.losses, dcLoss: value }
            })}
            highlight={highlightInputs}
          />
          
          <LossInput
            label="AC Loss"
            value={systemConfig.losses.acLoss}
            onChange={(value) => setSystemConfig({
              ...systemConfig,
              losses: { ...systemConfig.losses, acLoss: value }
            })}
            highlight={highlightInputs}
          />
          
          <LossInput
            label="Inverter Loss"
            value={systemConfig.losses.inverterLoss}
            onChange={(value) => setSystemConfig({
              ...systemConfig,
              losses: { ...systemConfig.losses, inverterLoss: value }
            })}
            highlight={highlightInputs}
          />
          
          <LossInput
            label="Degradation"
            value={systemConfig.losses.degradation}
            onChange={(value) => setSystemConfig({
              ...systemConfig,
              losses: { ...systemConfig.losses, degradation: value }
            })}
            highlight={highlightInputs}
          />
        </div>
        
        {/* Detection Summary Modal */}
        <DetectionSummaryModal
          isOpen={showModal}
          onClose={handleCancel}
          onApply={handleApplyChanges}
          latitude={location.latitude}
          longitude={location.longitude}
          locationName={location.city || location.country}
          systemType={systemConfig.systemType}
          panelType={systemConfig.panelType}
          capacity={systemConfig.capacity}
          detectedLosses={detectedLosses}
          previousLosses={previousLosses}
        />
        
        {/* Detection Loading Modal */}
        <DetectionLoadingModal
          isOpen={isAutoDetecting}
          currentStep={detectionStep}
        />
      </div>
    </div>
  );
}

// Step 3: Economic Parameters
function StepFinancial({
  financialParams,
  setFinancialParams,
}: {
  financialParams: FinancialParams;
  setFinancialParams: (params: FinancialParams) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-slate-900 mb-2">Economic Parameters</h3>
        <p className="text-slate-600">Configure investment costs and economic assumptions</p>
      </div>

      <div>
        <h4 className="font-semibold text-slate-900 mb-3">CAPEX (Capital Expenditure)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Panel Cost ($)
            </label>
            <DecimalInput
              value={financialParams.capex.panelCost}
              onChange={(value) => setFinancialParams({
                ...financialParams,
                capex: { ...financialParams.capex, panelCost: value }
              })}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Inverter Cost ($)
            </label>
            <DecimalInput
              value={financialParams.capex.inverterCost}
              onChange={(value) => setFinancialParams({
                ...financialParams,
                capex: { ...financialParams.capex, inverterCost: value }
              })}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              BOS Cost ($)
            </label>
            <DecimalInput
              value={financialParams.capex.bosCost}
              onChange={(value) => setFinancialParams({
                ...financialParams,
                capex: { ...financialParams.capex, bosCost: value }
              })}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Soft Costs ($)
            </label>
            <DecimalInput
              value={financialParams.capex.softCosts}
              onChange={(value) => setFinancialParams({
                ...financialParams,
                capex: { ...financialParams.capex, softCosts: value }
              })}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg"
              placeholder="0"
            />
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-slate-900 mb-3">OPEX (Operating Expenditure)</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Maintenance ($/year)
            </label>
            <DecimalInput
              value={financialParams.opex.maintenance}
              onChange={(value) => setFinancialParams({
                ...financialParams,
                opex: { ...financialParams.opex, maintenance: value }
              })}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Insurance ($/year)
            </label>
            <DecimalInput
              value={financialParams.opex.insurance}
              onChange={(value) => setFinancialParams({
                ...financialParams,
                opex: { ...financialParams.opex, insurance: value }
              })}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Monitoring ($/year)
            </label>
            <DecimalInput
              value={financialParams.opex.monitoring}
              onChange={(value) => setFinancialParams({
                ...financialParams,
                opex: { ...financialParams.opex, monitoring: value }
              })}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg"
              placeholder="0"
            />
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-slate-900 mb-3">Economic Parameters</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Electricity Price ($/kWh)
            </label>
            <DecimalInput
              value={financialParams.electricityPrice}
              onChange={(value) => setFinancialParams({ ...financialParams, electricityPrice: value })}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Price Escalation (%/year)
            </label>
            <DecimalInput
              value={financialParams.electricityPriceEscalation}
              onChange={(value) => setFinancialParams({ ...financialParams, electricityPriceEscalation: value })}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Discount Rate (%)
            </label>
            <DecimalInput
              value={financialParams.discountRate}
              onChange={(value) => setFinancialParams({ ...financialParams, discountRate: value })}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Project Lifetime
            </label>
            <div className="w-full px-4 py-3 bg-slate-100 border-2 border-slate-200 rounded-lg flex items-center justify-between">
              <span className="text-slate-900 font-medium">25 years</span>
              <span className="text-xs text-slate-500 bg-slate-200 px-2 py-1 rounded">Fixed</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              ℹ️ All analyses are calculated over a standard 25-year period
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 4: Review
function StepReview({
  location,
  systemConfig,
  financialParams,
}: {
  location: Location;
  systemConfig: SystemConfig;
  financialParams: FinancialParams;
}) {
  const totalCapex = Object.values(financialParams.capex).reduce((sum, val) => sum + (val || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-slate-900 mb-2">Review Your Configuration</h3>
        <p className="text-slate-600">Verify all details before calculating</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <h4 className="font-semibold text-slate-900 mb-3">Project Details</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Location:</span>
              <span className="font-medium text-slate-900">{location.latitude.toFixed(4)}°, {location.longitude.toFixed(4)}°</span>
            </div>
          </div>
        </div>

        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <h4 className="font-semibold text-slate-900 mb-3">System</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Capacity:</span>
              <span className="font-medium text-slate-900">{systemConfig.capacity} kWp</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Type:</span>
              <span className="font-medium text-slate-900">{systemConfig.panelType}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Inverter:</span>
              <span className="font-medium text-slate-900">{systemConfig.inverterEfficiency}%</span>
            </div>
          </div>
        </div>

        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <h4 className="font-semibold text-slate-900 mb-3">Investment</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Total CAPEX:</span>
              <span className="font-medium text-slate-900">${totalCapex.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Electricity Price:</span>
              <span className="font-medium text-slate-900">${financialParams.electricityPrice}/kWh</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Discount Rate:</span>
              <span className="font-medium text-slate-900">{financialParams.discountRate}%</span>
            </div>
          </div>
        </div>

        <div className="bg-[#4F819F]/10 rounded-lg p-4 border-2 border-[#4F819F]/30">
          <h4 className="font-semibold text-[#41474A] mb-2">Ready to Calculate</h4>
          <p className="text-sm text-[#41474A]">
            Click "Calculate & Save" to fetch real solar data from NASA POWER API and generate your detailed investment analysis.
          </p>
        </div>
      </div>
    </div>
  );
}